import { useState, useContext } from 'react';
import IconButton from '@mui/material/IconButton';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from "react-router-dom";
import { login, userConfirmForgotPassword, userForgotPassword } from '../../services/userService';
import { UserDispatchContext } from '../../context/UserContext';
import { useSnackbar } from 'notistack';


export const LoginForm = () => {
    const navigate = useNavigate();
    const globalUserDispatchContext = useContext(UserDispatchContext);
    const { enqueueSnackbar } = useSnackbar();

    const defaultErrorValues = {
        userNameError: false,
        passwordError: false,
        codeError: false,
        newPasswordError: false,
        confirmPasswordError: false
    };

    const [showPassword, setShowPassword] = useState(false);
    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const [showForgotNewPassword, setShowForgotNewPassword] = useState(false);
    const [showForgotConfirmPassword, setShowForgotConfirmPassword] = useState(false);
    const [errors, setErrors] = useState(defaultErrorValues);
    const [authView, setAuthView] = useState('login');
    const [isLoading, setIsLoading] = useState(false);
    const [userForm, setUserForm] = useState({
        userName: '',
        password: ''
    });
    const [forgotForm, setForgotForm] = useState({
        code: '',
        newPassword: '',
        confirmPassword: ''
    });
    const { userName, password } = userForm;
    const { userNameError, passwordError, codeError, newPasswordError, confirmPasswordError } = errors;
    const updateUserForm = (e) => {
        let inputName = e.target.name;
        let value = e.target.value;
        setUserForm({
            ...userForm,
            [inputName]: value
        });
        errors[`${inputName}Error`] && setErrors({ ...errors, [`${inputName}Error`]: false });
    }

    const updateForgotForm = (e) => {
        let inputName = e.target.name;
        let value = e.target.value;
        setForgotForm({
            ...forgotForm,
            [inputName]: value
        });
        errors[`${inputName}Error`] && setErrors({ ...errors, [`${inputName}Error`]: false });
    }

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const OnSubmitForm = async (evt) => {
        evt.preventDefault();
        if (isLoading) {
            return;
        }

        if (!userName || !password) {
            setErrors({
                ...errors,
                userNameError: !userName,
                passwordError: !password
            });
            enqueueSnackbar('Debes completar usuario y contraseña.', {
                variant: 'error',
                autoHideDuration: 3000
            });
            return;
        }

        setIsLoading(true);
        const result = await login(userName, password);
        setIsLoading(false);

        if (result.AccessToken) {
            globalUserDispatchContext({
                type: 'LOG_IN',
                AccessToken: result.AccessToken,
                RefreshToken: result.RefreshToken,
                uid: result.uid
            });
            navigate('/admin/products');
        } else {
            setErrors({
                userNameError: true,
                passwordError: true
            });
            setUserForm({
                ...userForm,
                password: ''
            });
            enqueueSnackbar("Usuario y/o contraseña incorrecta.", {
                variant: 'error',
                autoHideDuration: 3000
            });
        }
    }

    const OnRequestForgotPassword = async () => {
        if (isLoading) {
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const hasValidUserName = !!userName && emailRegex.test(userName);

        if (!hasValidUserName) {
            setErrors({ ...errors, userNameError: true });
            enqueueSnackbar('Ingresa un correo electrónico válido.', {
                variant: 'error',
                autoHideDuration: 3000
            });
            return;
        }

        setIsLoading(true);
        const result = await userForgotPassword(userName.trim());
        setIsLoading(false);

        if (result.result) {
            enqueueSnackbar(result.message, {
                variant: 'success',
                autoHideDuration: 3000
            });
            setAuthView('reset');
            return;
        }

        enqueueSnackbar(result.message || 'No se pudo enviar el código de recuperación.', {
            variant: 'error',
            autoHideDuration: 3000
        });
    }

    const OnConfirmForgotPassword = async () => {
        if (isLoading) {
            return;
        }

        const nextErrors = {
            codeError: !forgotForm.code,
            newPasswordError: !forgotForm.newPassword,
            confirmPasswordError: !forgotForm.confirmPassword
        };

        if (forgotForm.newPassword && forgotForm.confirmPassword && forgotForm.newPassword !== forgotForm.confirmPassword) {
            nextErrors.confirmPasswordError = true;
        }

        if (Object.values(nextErrors).some(Boolean)) {
            setErrors({ ...errors, ...nextErrors });
            enqueueSnackbar('Completa correctamente los campos para restablecer tu contraseña.', {
                variant: 'error',
                autoHideDuration: 3000
            });
            return;
        }

        setIsLoading(true);
        const result = await userConfirmForgotPassword({
            username: userName.trim(),
            code: forgotForm.code.trim(),
            password: forgotForm.newPassword
        });
        setIsLoading(false);

        if (result.result) {
            enqueueSnackbar(result.message, {
                variant: 'success',
                autoHideDuration: 3000
            });
            setForgotForm({
                code: '',
                newPassword: '',
                confirmPassword: ''
            });
            setUserForm({
                ...userForm,
                password: ''
            });
            setAuthView('login');
            return;
        }

        enqueueSnackbar(result.message || 'No se pudo restablecer la contraseña.', {
            variant: 'error',
            autoHideDuration: 3000
        });
    }


    return (
        <div className='mt-6 xl:mt-8 w-full'>
            {authView === 'login' && (
                <form onSubmit={OnSubmitForm}>
                    <div className='mb-2 xl:mb-4'>
                        <TextField
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '36px',
                                    height: '47px',
                                    color: 'white',
                                    "&.Mui-focused fieldset": {
                                        borderColor: 'white'
                                    }
                                },
                                '& .MuiOutlinedInput-root fieldset': {
                                    borderColor: '#ffffff8c',
                                    borderWidth: '2px',
                                    height: '52px'
                                },
                                '& .MuiOutlinedInput-input': {
                                    background: 'inherit',
                                    height: '28px',
                                    textAlign: 'center',
                                    autoComplete: 'off'
                                }
                            }}
                            error={userNameError}
                            id="outlined-basic"
                            placeholder='Usuario'
                            fullWidth
                            variant="outlined"
                            size="small"
                            name={"userName"}
                            value={userName}
                            onChange={updateUserForm}
                        />
                    </div>
                    <FormControl
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '36px',
                                height: '47px',
                                color: 'white',
                                "&.Mui-focused fieldset": {
                                    borderColor: 'white'
                                }
                            },
                            '& .MuiOutlinedInput-root fieldset': {
                                borderColor: '#ffffff8c',
                                borderWidth: '2px',
                                height: '52px',
                            },
                            '& .MuiOutlinedInput-input': {
                                background: 'inherit',
                                height: '28px',
                                textAlign: 'center',
                                autoComplete: "off",
                                paddingLeft: '42px',
                            },
                            '& .MuiInputAdornment-root': {
                                marginLeft: 0
                            },
                            '& .MuiSvgIcon-root': {
                                color: 'white'
                            },
                            m: 1, width: '100%', margin: 0
                        }}
                        variant="outlined">
                        <OutlinedInput
                            fullWidth
                            size="small"
                            id="outlined-adornment-password"
                            type={showPassword ? 'text' : 'password'}
                            name={"password"}
                            value={password}
                            placeholder='Contraseña'
                            onChange={updateUserForm}
                            error={passwordError}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                    </FormControl>
                    <button disabled={isLoading} type='submit' className='w-full h-12 mt-4 xl:mt-6 bg-admin-primary text-white text-base font-bold font-inter rounded-full disabled:opacity-70'>Ingresar</button>
                    <button
                        type='button'
                        className='w-full text-sm text-white underline mt-4'
                        onClick={() => setAuthView('forgot')}
                    >
                        ¿Olvidaste tu contraseña?
                    </button>
                </form>
            )}

            {authView === 'forgot' && (
                <div>
                    <div className='mb-2 xl:mb-4'>
                        <TextField
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '36px',
                                    height: '47px',
                                    color: 'white',
                                    "&.Mui-focused fieldset": {
                                        borderColor: 'white'
                                    }
                                },
                                '& .MuiOutlinedInput-root fieldset': {
                                    borderColor: '#ffffff8c',
                                    borderWidth: '2px',
                                    height: '52px'
                                },
                                '& .MuiOutlinedInput-input': {
                                    background: 'inherit',
                                    height: '28px',
                                    textAlign: 'center',
                                    autoComplete: 'off'
                                }
                            }}
                            error={userNameError}
                            placeholder='Correo electrónico'
                            fullWidth
                            variant='outlined'
                            size='small'
                            name='userName'
                            value={userName}
                            onChange={updateUserForm}
                        />
                    </div>
                    <button disabled={isLoading} type='button' className='w-full h-12 mt-4 bg-admin-primary text-white text-base font-bold font-inter rounded-full disabled:opacity-70' onClick={OnRequestForgotPassword}>Enviar código</button>
                    <button type='button' className='w-full text-sm text-white underline mt-4' onClick={() => setAuthView('login')}>Volver a inicio de sesión</button>
                </div>
            )}

            {authView === 'reset' && (
                <div>
                    <div className='mb-2 xl:mb-4'>
                        <TextField
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '36px',
                                    height: '47px',
                                    color: 'white',
                                    "&.Mui-focused fieldset": {
                                        borderColor: 'white'
                                    }
                                },
                                '& .MuiOutlinedInput-root fieldset': {
                                    borderColor: '#ffffff8c',
                                    borderWidth: '2px',
                                    height: '52px'
                                },
                                '& .MuiOutlinedInput-input': {
                                    background: 'inherit',
                                    height: '28px',
                                    textAlign: 'center',
                                    autoComplete: 'off'
                                }
                            }}
                            error={codeError}
                            placeholder='Código de verificación'
                            fullWidth
                            variant='outlined'
                            size='small'
                            name='code'
                            value={forgotForm.code}
                            onChange={updateForgotForm}
                        />
                    </div>
                    <div className='mb-2 xl:mb-4'>
                        <TextField
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '36px',
                                    height: '47px',
                                    color: 'white',
                                    "&.Mui-focused fieldset": {
                                        borderColor: 'white'
                                    },
                                    "&.Mui-focused .MuiSvgIcon-root": {
                                        color: 'white'
                                    },
                                },
                                '& .MuiOutlinedInput-root fieldset': {
                                    borderColor: '#ffffff8c',
                                    borderWidth: '2px',
                                    height: '52px'
                                },
                                '& .MuiOutlinedInput-input': {
                                    background: 'inherit',
                                    height: '28px',
                                    textAlign: 'center',
                                    autoComplete: 'off'
                                },
                                '& .MuiSvgIcon-root': {
                                    color: '#ffffff8c'
                                },
                            }}
                            type={showForgotNewPassword ? 'text' : 'password'}
                            error={newPasswordError}
                            placeholder='Nueva contraseña'
                            fullWidth
                            variant='outlined'
                            size='small'
                            name='newPassword'
                            value={forgotForm.newPassword}
                            onChange={updateForgotForm}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle new password visibility"
                                            onClick={() => setShowForgotNewPassword((show) => !show)}
                                            onMouseDown={handleMouseDownPassword}
                                            edge="end"
                                        >
                                            {showForgotNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </div>
                    <div className='mb-2 xl:mb-4'>
                        <TextField
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '36px',
                                    height: '47px',
                                    color: 'white',
                                    "&.Mui-focused fieldset": {
                                        borderColor: 'white'
                                    },
                                    "&.Mui-focused .MuiSvgIcon-root": {
                                        color: 'white'
                                    },
                                },
                                '& .MuiOutlinedInput-root fieldset': {
                                    borderColor: '#ffffff8c',
                                    borderWidth: '2px',
                                    height: '52px'
                                },
                                '& .MuiOutlinedInput-input': {
                                    background: 'inherit',
                                    height: '28px',
                                    textAlign: 'center',
                                    autoComplete: 'off'
                                },
                                '& .MuiSvgIcon-root': {
                                    color: '#ffffff8c'
                                },
                            }}
                            type={showForgotConfirmPassword ? 'text' : 'password'}
                            error={confirmPasswordError}
                            placeholder='Confirmar nueva contraseña'
                            fullWidth
                            variant='outlined'
                            size='small'
                            name='confirmPassword'
                            value={forgotForm.confirmPassword}
                            onChange={updateForgotForm}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle confirm password visibility"
                                            onClick={() => setShowForgotConfirmPassword((show) => !show)}
                                            onMouseDown={handleMouseDownPassword}
                                            edge="end"
                                        >
                                            {showForgotConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </div>
                    <button disabled={isLoading} type='button' className='w-full h-12 mt-4 bg-admin-primary text-white text-base font-bold font-inter rounded-full disabled:opacity-70' onClick={OnConfirmForgotPassword}>Restablecer contraseña</button>
                    <button type='button' className='w-full text-sm text-white underline mt-4' onClick={OnRequestForgotPassword}>Reenviar código</button>
                    <button type='button' className='w-full text-sm text-white underline mt-2' onClick={() => setAuthView('login')}>Volver a inicio de sesión</button>
                </div>
            )}
        </div>
    )
}


